import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileDown, Printer, X } from "lucide-react";
import { API } from "../lib/api";

const sectionOptions = [
  ["details", "Daftar kategori dan uraian pengajuan"],
  ["summary", "Ringkasan anggaran"],
  ["period", "Periode"],
  ["receipts", "Lampiran foto nota"],
  ["items", "Lampiran bukti pembelian barang"],
];

export const PdfExport = ({ month }) => {
  const [scope, setScope] = useState("approved");
  const [sections, setSections] = useState(["details", "period", "receipts", "items"]);
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const frameRef = useRef(null);

  const toggleSection = value => setSections(current => current.includes(value) ? current.filter(item => item !== value) : [...current, value]);

  useEffect(() => {
    if (!open || !sections.length) {
      setPreviewUrl("");
      setLoading(false);
      setError("");
      return undefined;
    }
    const controller = new AbortController();
    let objectUrl = "";
    setPreviewUrl("");
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/reports/pdf`, { params: { month, scope, sections: sections.join(",") }, responseType: "blob", signal: controller.signal });
        objectUrl = URL.createObjectURL(data);
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!controller.signal.aborted) setError(err.response?.data?.detail || "Pratinjau PDF gagal dibuat.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, month, scope, sections]);

  const download = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl; a.download = `laporan-pengeluaran-${month}-${scope}.pdf`; a.click();
    toast.success("Laporan PDF berhasil diunduh");
  };

  const print = () => {
    if (!previewUrl) return;
    const frameWindow = frameRef.current?.contentWindow;
    if (frameWindow) {
      frameWindow.focus();
      frameWindow.print();
    } else {
      const printWindow = window.open(previewUrl, "_blank");
      if (!printWindow) { toast.error("Izinkan pop-up browser untuk mencetak PDF"); return; }
      printWindow.onload = () => printWindow.print();
    }
  };

  return <>
    <button data-testid="report-pdf-open" className="primary-btn" onClick={() => setOpen(true)}><FileDown size={16} /> Buat PDF</button>
    {open && <div className="pdf-preview-backdrop" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
      <section className="pdf-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="pdf-preview-title">
        <header className="pdf-preview-header"><div><p className="eyebrow">LAPORAN {month}</p><h2 id="pdf-preview-title">Pratinjau PDF</h2><p className="muted">Atur isi laporan, periksa hasilnya, lalu unduh atau cetak.</p></div><button type="button" className="icon-btn" aria-label="Tutup pratinjau" onClick={() => setOpen(false)}><X size={20} /></button></header>
        <div className="pdf-preview-layout">
          <aside className="pdf-preview-controls">
            <label className="pdf-filter-label">Cakupan transaksi
              <select data-testid="report-pdf-scope" value={scope} onChange={e => setScope(e.target.value)}><option value="approved">Hanya disetujui</option><option value="all">Semua status</option></select>
            </label>
            <fieldset className="pdf-preview-sections"><legend>Bagian laporan</legend>{sectionOptions.map(([value, label]) => <label key={value}><input type="checkbox" checked={sections.includes(value)} onChange={() => toggleSection(value)} />{label}</label>)}</fieldset>
            <div className="pdf-preview-actions">
              <button data-testid="report-export-pdf" className="primary-btn" onClick={download} disabled={!previewUrl || loading}><Download size={16} /> Unduh PDF</button>
              <button data-testid="report-print" className="secondary-btn" onClick={print} disabled={!previewUrl || loading}><Printer size={16} /> Cetak PDF</button>
            </div>
            {!sections.length && <p className="pdf-preview-hint">Pilih setidaknya satu bagian laporan untuk membuat pratinjau.</p>}
          </aside>
          <div className="pdf-preview-document" aria-live="polite">
            {loading && <div className="pdf-preview-message">Sedang menyiapkan pratinjau...</div>}
            {!loading && error && <div className="pdf-preview-message error">{error}</div>}
            {!loading && !error && !sections.length && <div className="pdf-preview-message">Pilih bagian laporan untuk melihat pratinjau.</div>}
            {previewUrl && !loading && <iframe ref={frameRef} title="Pratinjau dokumen laporan PDF" src={previewUrl} />}
          </div>
        </div>
      </section>
    </div>}
  </>;
};
